import {Button, Group, Input, Stack, Text, TextInput} from '@mantine/core';
import React, {useEffect, useState, useCallback} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {SpreadsheetLocaleSelector} from 'bulk-import/panes/SpreadsheetLocaleSelector';
import UserConstants from 'constants/UserConstants';
import {usePollForDatasetProcessingToComplete} from 'data-frames/graphql/usePollForDatasetProcessingToComplete';
import {createDatasetFromFile} from 'data-frames/network/DatasetDAO';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {DEFAULT_LANGUAGE_TAG} from 'i18n/LanguageTagConstants';
import notifications from 'notifications';
import preferences from 'Preferences';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import ModalFooter from 'ui-library/modals/ModalFooter';
import type {RunModalProps} from 'ui-library/runModal';
import {CenteredSpinner} from 'ui-library/Spinner';
import {upload} from 'util/ImportUtil';
import {getUserMessage} from 'util/Net';

const ROOT_TEST_PROPS = getTestProps({component: 'CreateDatasetModal', element: 'root'});

export interface CreateDatasetModalProps extends RunModalProps<string | null> {
  initialFolderId?: string;
}

/**
 * Modal to create a dataset by uploading a raw file.
 *
 * The modal stays open after the dataset is created and polls until its data finishes processing, so
 * callers can use the dataset immediately; callers should still check that the dataset was processed
 * successfully.
 */
export default function CreateDatasetModal({close, initialFolderId}: CreateDatasetModalProps): JSX.Element {
  const [datasetId, setDatasetId] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const {queryStatus, dataset, dataFrame} = usePollForDatasetProcessingToComplete(datasetId);

  useEffect(() => {
    if (queryStatus === QueryResultType.ERROR) {
      close(null); // Do not return the `datasetId` if we know it cannot be loaded
    } else if (queryStatus === QueryResultType.SUCCESS && dataFrame?.uploadStatus !== 'IN_PROGRESS') {
      close(datasetId); // Return the `datasetId` once processing completes, even if processing failed
    }
  }, [queryStatus, dataset, dataFrame, datasetId, close]);

  const onClose = useCallback(() => {
    close(null);
  }, [close]);

  const onSave: onSaveCallback = useCallback(
    async ({name, folderId, file, studyIds, locale}) => {
      try {
        setLoadingMessage('Uploading file...');
        const s3Location = await upload(file);
        if (s3Location == null) {
          throw new Error('Failed to upload file.');
        }
        setLoadingMessage('Creating dataset...');
        const {createdIds} = await createDatasetFromFile({
          name,
          folderId,
          s3Location,
          studyIds,
          ...(locale != null && {locale}),
        });
        setDatasetId(createdIds[0]);
        setLoadingMessage('Processing dataset...');
        // This `catch` handles notifying the user and closing the modal
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (error) {
        notifications.error(getUserMessage(error) ?? 'Failed to create dataset.');
        close(null);
      }
    },
    [close]
  );

  return loadingMessage ? (
    <Stack spacing="md" py="xl" align="center" {...ROOT_TEST_PROPS}>
      <CenteredSpinner aria-label="Processing dataset" />
      <Text size="sm" color="dimmed">
        {loadingMessage}
      </Text>
    </Stack>
  ) : (
    <CreateDatasetModalConfigurationForm
      initialFolderId={initialFolderId}
      onClose={onClose}
      onSave={onSave}
    />
  );
}

type onSaveCallback = (args: {
  name: string;
  folderId: string;
  file: File;
  studyIds: Array<string>;
  locale?: string;
}) => Promise<void>;

interface ConfigurationPhaseProps {
  initialFolderId?: string;
  onClose: () => void;
  onSave: onSaveCallback;
}

function CreateDatasetModalConfigurationForm({
  initialFolderId,
  onClose,
  onSave,
}: ConfigurationPhaseProps): JSX.Element {
  const [name, setName] = useState('');
  const [folderId, setFolderId] = useState<string | null>(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.DATASET) ?? null
  );
  const [file, setFile] = useState<File | null>(null);
  const [studies, setStudies] = useState<Array<StudySearchItem>>([]);
  const [locale, setLocale] = useState(
    preferences.get(UserConstants.SPREADSHEET_IMPORT_LANGUAGE_TAG) ?? DEFAULT_LANGUAGE_TAG.code
  );

  const canSubmit = name.trim().length > 0 && folderId != null && file != null;

  function handleSubmit(): void {
    if (name.trim().length > 0 && folderId != null && file != null) {
      FolderDefaults.set(FolderDefaultsKey.DATASET, folderId);
      onSave({
        name,
        folderId,
        file,
        studyIds: studies.map((study) => study.id),
        ...(BenchlingConfig.NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD && {locale}),
      });
    }
  }

  return (
    <Stack spacing="md" {...ROOT_TEST_PROPS}>
      <TextInput
        label="Name"
        required={true}
        data-autofocus={true}
        value={name}
        onChange={(e) => {
          setName(e.currentTarget.value);
        }}
      />
      <Input.Wrapper label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} required={true}>
        <FolderSelector
          enableRedesign={true}
          level="append"
          // This prop allows us to select non-root folders
          showNotebook={true}
          value={folderId}
          onChange={(e) => {
            setFolderId(e.target.value);
          }}
          unsetOnFetchFail={true}
          selectorModalZIndex={ZINDEX_FLOATING_UI as 1060}
        />
      </Input.Wrapper>
      <Input.Wrapper label="File" required={true}>
        {file == null ? (
          <FileChooser
            multiple={false}
            onSelectFiles={(file) => {
              setFile(file);
              setName((currentName) => (currentName.trim().length === 0 ? file.name : currentName));
            }}
          />
        ) : (
          <FileList>
            <FileRow
              file={file}
              onRequestRemove={() => {
                setFile(null);
              }}
            />
          </FileList>
        )}
      </Input.Wrapper>
      {BenchlingConfig.NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD && (
        <SpreadsheetLocaleSelector languageTag={locale} onChange={setLocale} />
      )}
      {BenchlingConfig.STUDIES_ENABLE && (
        <Input.Wrapper label="Studies">
          <StudySearchInput
            selectedItems={studies}
            onChange={(items) => {
              setStudies(items as Array<StudySearchItem>);
            }}
            shouldAllowMultiple={true}
          />
        </Input.Wrapper>
      )}
      <ModalFooter>
        <Group position="right">
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
            Create
          </Button>
        </Group>
      </ModalFooter>
    </Stack>
  );
}
